/**
 * Feature 9 — Vendor's own disputes list.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import { disputesApi } from '../../services/disputes';


function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    under_review: 'bg-amber-50 text-amber-700 border-amber-200',
    more_info_requested: 'bg-slate-100 text-slate-700 border-slate-200',
    resolved_upheld: 'bg-slate-100 text-slate-700 border-slate-200',
    resolved_corrected: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    resolved_confidence_adjusted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    resolved_data_excluded: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dismissed: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${map[status] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}


export default function VendorDisputes() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['vendor', 'disputes', page, statusFilter],
    queryFn: () => disputesApi.list({ page: String(page), status: statusFilter || undefined }),
  });

  const totalPages = data ? Math.ceil(data.count / 20) : 1;

  return (
    <>
      <PageHeader title="Disputes" description="Reviews you've requested" />
      <div className="mb-4">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="rounded-xl border-2 border-slate-200 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
        >
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="under_review">Under review</option>
          <option value="more_info_requested">More info requested</option>
          <option value="resolved_upheld">Resolved — upheld</option>
          <option value="resolved_corrected">Resolved — corrected</option>
          <option value="dismissed">Dismissed</option>
        </select>
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
        {isLoading ? (
          <div className="p-12 flex justify-center"><LoadingSpinner size="md" /></div>
        ) : !data || data.results.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">No disputes yet.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Reason</th>
                <th className="px-4 py-3 text-left font-medium">Severity</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {data.results.map((d) => (
                <tr key={d.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-sm text-slate-800">
                    <div>{d.reason_display}</div>
                    <div className="text-xs text-slate-500 line-clamp-2 max-w-md">
                      {d.justification}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-medium ${d.severity === 'critical' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                      {d.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {new Date(d.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && totalPages > 1 && (
        <div className="mt-4 flex items-center justify-end gap-2 text-sm">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-50">Previous</button>
          <span className="text-slate-500">Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="rounded-lg border border-slate-200 px-3 py-1 disabled:opacity-50">Next</button>
        </div>
      )}
    </>
  );
}
